/**
 * GovernanceFilingPanel
 * Handles filing generated documents to the governance_documents table
 * Includes loading states and error handling
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { FolderOpen, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import type { GeneratedDocument, GovernanceDocument } from "./types/workflow-components.types";

export interface GovernanceFilingPanelProps {
  taskId: string;
  unaId: string;
  documents: GeneratedDocument[];
  workflowKey?: string;
  onFilingComplete?: () => void;
}

interface FilingStatus {
  documentId: string;
  isFiled: boolean;
  governanceDocId?: string;
  filedAt?: string;
}

// Helper function to map workflow to category
const getCategoryForWorkflow = (workflowKey: string): { type: string; category: string } => {
  const mapping: Record<string, { type: string; category: string }> = {
    'asset_transfers_contributions': {
      type: 'contribution_documents',
      category: 'contributions'
    },
    'conducting_meeting': {
      type: 'meeting_materials',
      category: 'Meeting Prep'
    },
    'conducting_a_meeting': {
      type: 'meeting_materials',
      category: 'Meeting Prep'
    },
    'recording_minutes': {
      type: 'meeting_minutes',
      category: 'meetings'
    },
    'add_members': {
      type: 'membership_documents',
      category: 'membership'
    },
    'add_member_simple': {
      type: 'membership_documents',
      category: 'membership'
    },
    'add_officer': {
      type: 'officer_documents',
      category: 'officers'
    },
    'writing_resolution': {
      type: 'resolution_documents',
      category: 'resolutions'
    },
  };
  
  return mapping[workflowKey] || { type: 'general', category: 'governance' };
};

export const GovernanceFilingPanel = ({
  taskId,
  unaId,
  documents,
  workflowKey,
  onFilingComplete
}: GovernanceFilingPanelProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [filingStatuses, setFilingStatuses] = useState<Record<string, FilingStatus>>({});
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [filing, setFiling] = useState(false);

  // Check which documents are already filed
  useEffect(() => {
    checkFilingStatus();
  }, [documents]);

  const checkFilingStatus = async () => {
    if (documents.length === 0) return;

    setCheckingStatus(true);
    try {
      const { data, error } = await supabase
        .from('governance_documents')
        .select('id, original_file_url, created_at')
        .eq('una_id', unaId)
        .eq('generated_from_task_id', taskId);

      if (error) throw error;

      // Map governance docs to generated docs by file_url (prefer signed version)
      const statusMap: Record<string, FilingStatus> = {};
      
      documents.forEach(doc => {
        const fileUrlToMatch = doc.signed_file_url || doc.file_url;
        const govDoc = data?.find(gd => gd.original_file_url === fileUrlToMatch);
        statusMap[doc.id] = {
          documentId: doc.id,
          isFiled: !!govDoc,
          governanceDocId: govDoc?.id,
          filedAt: govDoc?.created_at
        };
      });

      setFilingStatuses(statusMap);
    } catch (error) {
      console.error('Error checking filing status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const toggleDocumentSelection = (documentId: string) => {
    const newSet = new Set(selectedDocuments);
    if (newSet.has(documentId)) {
      newSet.delete(documentId);
    } else {
      newSet.add(documentId);
    }
    setSelectedDocuments(newSet);
  };

  const selectAll = () => {
    const unfiledDocs = documents.filter(doc => !filingStatuses[doc.id]?.isFiled);
    setSelectedDocuments(new Set(unfiledDocs.map(d => d.id)));
  };

  const handleFileDocuments = async () => {
    if (selectedDocuments.size === 0) {
      toast({
        title: 'Selection Required',
        description: 'Please select at least one document to file',
        variant: 'destructive'
      });
      return;
    }

    setFiling(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const docsToFile = documents.filter(d => selectedDocuments.has(d.id));
      const { type, category } = getCategoryForWorkflow(workflowKey || '');
      
      const insertPromises = docsToFile.map(doc => 
        supabase
          .from('governance_documents')
          .insert({
            una_id: unaId,
            document_type: type,
            document_category: category,
            // Use uploaded filename if available, otherwise fall back to document_name
            document_title: doc.uploaded_filename || doc.document_name,
            original_file_url: doc.signed_file_url || doc.file_url,
            signed_file_url: doc.signed_file_url || doc.file_url,
            generated_from_task_id: taskId,
            effective_date: new Date().toISOString(),
            access_level: 'all_members',
            all_signatures_collected: doc.status === 'final' || doc.status === 'finalized' || !!doc.signed_file_url,
            created_by: user.id
          })
      );

      const results = await Promise.allSettled(insertPromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (successful > 0) {
        toast({
          title: 'Documents Filed',
          description: `Successfully filed ${successful} document(s) to governance records`,
        });
      }

      if (failed > 0) {
        toast({
          title: 'Some Filings Failed',
          description: `${failed} document(s) failed to file`,
          variant: 'destructive'
        });
      }

      // Refresh status and clear selection
      await checkFilingStatus();
      setSelectedDocuments(new Set());
      onFilingComplete?.();
    } catch (error: any) {
      console.error('Filing error:', error);
      toast({
        title: 'Filing Failed',
        description: error.message || 'Failed to file documents',
        variant: 'destructive'
      });
    } finally {
      setFiling(false);
    }
  };

  const handleViewGovernanceRecords = () => {
    navigate('/governance-documents');
  };

  const unfiledCount = documents.filter(doc => !filingStatuses[doc.id]?.isFiled).length;
  const filedCount = documents.filter(doc => filingStatuses[doc.id]?.isFiled).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Governance Filing
          </div>
          {filedCount > 0 && (
            <Badge variant="outline" className="text-xs">
              {filedCount} of {documents.length} filed
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {checkingStatus ? (
          <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Checking filing status...
          </div>
        ) : (
          <>
            {/* Document List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Documents to File</div>
                {unfiledCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                  >
                    Select All Unfiled
                  </Button>
                )}
              </div>
              
              {documents.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  No documents available to file
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => {
                    const status = filingStatuses[doc.id];
                    const isDisabled = status?.isFiled;
                    
                    return (
                      <div
                        key={doc.id}
                        className={`flex items-center justify-between p-2 border rounded ${
                          isDisabled ? 'bg-muted opacity-60' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <Checkbox
                            checked={selectedDocuments.has(doc.id)}
                            onCheckedChange={() => toggleDocumentSelection(doc.id)}
                            disabled={isDisabled}
                          />
                          <div className="flex-1">
                            <div className="text-sm">{doc.document_name}</div>
                            {status?.isFiled && status.filedAt && (
                              <div className="text-xs text-muted-foreground">
                                Filed {new Date(status.filedAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                        {status?.isFiled ? (
                          <Badge variant="default" className="text-xs bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Filed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            Not Filed
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {unfiledCount > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Button
                    onClick={handleFileDocuments}
                    disabled={filing || selectedDocuments.size === 0}
                    className="w-full"
                  >
                    {filing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Filing...
                      </>
                    ) : (
                      <>
                        <FolderOpen className="h-4 w-4 mr-2" />
                        File {selectedDocuments.size} Document(s)
                      </>
                    )}
                  </Button>
                  
                  {selectedDocuments.size === 0 && (
                    <p className="text-xs text-muted-foreground text-center">
                      Select documents to file to governance records
                    </p>
                  )}
                </div>
              </>
            )}

            {/* View Governance Records Link */}
            {filedCount > 0 && (
              <>
                <Separator />
                <Button
                  variant="outline"
                  onClick={handleViewGovernanceRecords}
                  className="w-full"
                >
                  View Governance Records
                </Button>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
